import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { organizationsAPI, platformAPI, nichesAPI } from '../lib/api';
import { formatDate, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    Building2,
    Plus,
    Users,
    Package,
    Crown,
    Search,
    RefreshCw,
    Edit,
    UserPlus,
    CreditCard,
    CheckCircle,
    Clock,
    XCircle,
    TrendingUp,
    SprayCan,
    Shirt,
    Smartphone,
    Home,
    Dumbbell,
    Gem,
    HeartPulse,
    ArrowLeft,
    ShoppingCart,
    Settings,
    Zap,
    MessageSquare,
    Globe
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Niche icons mapping
const NICHE_ICONS = {
    'parfum_cosmetique': SprayCan,
    'vetements_mode': Shirt,
    'electronique': Smartphone,
    'maison_deco': Home,
    'sport_loisirs': Dumbbell,
    'bijoux_accessoires': Gem,
    'sante_bienetre': HeartPulse,
    'autre': Package
};

const PLAN_LABELS = {
    starter: { label: 'Starter', color: 'bg-gray-100 text-gray-700', icon: Package },
    pro: { label: 'Pro', color: 'bg-blue-100 text-blue-700', icon: TrendingUp },
    enterprise: { label: 'Enterprise', color: 'bg-purple-100 text-purple-700', icon: Crown },
};

const STATUS_LABELS = {
    trial: { label: 'Essai', color: 'bg-amber-100 text-amber-700', icon: Clock },
    active: { label: 'Actif', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    expired: { label: 'Expiré', color: 'bg-red-100 text-red-700', icon: XCircle },
    cancelled: { label: 'Annulé', color: 'bg-gray-100 text-gray-700', icon: XCircle },
};

export default function SuperAdminDashboard() {
    const { user } = useAuth();
    const [view, setView] = useState('niches'); // 'niches' or 'organizations'
    const [selectedNiche, setSelectedNiche] = useState(null);
    const [niches, setNiches] = useState([]);
    const [nicheStats, setNicheStats] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [platformStats, setPlatformStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    
    // Dialogs
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [adminDialogOpen, setAdminDialogOpen] = useState(false);
    const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
    const [integrationsDialogOpen, setIntegrationsDialogOpen] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);
    
    // Form states
    const [newOrg, setNewOrg] = useState({ 
        name: '', 
        slug: '', 
        niche: '',
        email: '', 
        phone: '', 
        wilaya: '',
        shopify_shop_url: '',
        shopify_access_token: '',
        shopify_storefront_url: '',
        telegram_bot_token: '',
        twilio_account_sid: '',
        twilio_auth_token: '',
        twilio_messaging_service_sid: ''
    });
    const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '' });
    const [newSubscription, setNewSubscription] = useState({ plan: 'pro', duration_months: 1 });
    const [submitting, setSubmitting] = useState(false);

    // Fetch niches and stats
    const fetchNiches = useCallback(async () => {
        try {
            const [nichesRes, statsRes, platformStatsRes] = await Promise.all([
                nichesAPI.getAll(),
                platformAPI.getStatsByNiche(),
                platformAPI.getStats()
            ]);
            setNiches(nichesRes.data);
            setNicheStats(statsRes.data);
            setPlatformStats(platformStatsRes.data);
        } catch (error) {
            console.error('Error fetching niches:', error);
            toast.error('Erreur lors du chargement des niches');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch organizations for selected niche
    const fetchOrganizations = useCallback(async (niche = null) => {
        try {
            setLoading(true);
            const res = await organizationsAPI.getAll(niche);
            setOrganizations(res.data);
        } catch (error) {
            console.error('Error fetching organizations:', error);
            toast.error('Erreur lors du chargement des organisations');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user?.role === 'super_admin') {
            fetchNiches();
        }
    }, [fetchNiches, user]);

    // Handle niche selection
    const handleSelectNiche = (niche) => {
        setSelectedNiche(niche);
        setView('organizations');
        fetchOrganizations(niche.id);
        setNewOrg(prev => ({ ...prev, niche: niche.id }));
    };

    // Go back to niches view
    const handleBackToNiches = () => {
        setSelectedNiche(null);
        setView('niches');
        setOrganizations([]);
        fetchNiches();
    };

    // Filter organizations
    const filteredOrgs = organizations.filter(org =>
        org.name.toLowerCase().includes(search.toLowerCase()) ||
        org.slug.toLowerCase().includes(search.toLowerCase())
    );

    // Create organization
    const handleCreateOrg = async () => {
        if (!newOrg.name || !newOrg.slug || !newOrg.niche) {
            toast.error('Nom, slug et niche sont requis');
            return;
        }
        
        setSubmitting(true);
        try {
            await organizationsAPI.create(newOrg);
            toast.success('Organisation créée!');
            setCreateDialogOpen(false);
            setNewOrg({ 
                name: '', 
                slug: '', 
                niche: selectedNiche?.id || '',
                email: '', 
                phone: '', 
                wilaya: '',
                shopify_shop_url: '',
                shopify_access_token: '',
                shopify_storefront_url: '',
                telegram_bot_token: '',
                twilio_account_sid: '',
                twilio_auth_token: '',
                twilio_messaging_service_sid: ''
            });
            if (selectedNiche) {
                fetchOrganizations(selectedNiche.id);
            }
            fetchNiches();
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la création';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    // Update organization
    const handleUpdateOrg = async () => {
        if (!selectedOrg) return;
        
        setSubmitting(true);
        try {
            await organizationsAPI.update(selectedOrg.id, selectedOrg);
            toast.success('Organisation mise à jour!');
            setEditDialogOpen(false);
            if (selectedNiche) {
                fetchOrganizations(selectedNiche.id);
            }
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la mise à jour';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    // Create admin for org
    const handleCreateAdmin = async () => {
        if (!selectedOrg || !newAdmin.name || !newAdmin.email || !newAdmin.password) {
            toast.error('Tous les champs sont requis');
            return;
        }
        
        setSubmitting(true);
        try {
            await organizationsAPI.createAdmin(selectedOrg.id, newAdmin);
            toast.success(`Admin créé pour ${selectedOrg.name}!`);
            setAdminDialogOpen(false);
            setNewAdmin({ name: '', email: '', password: '' });
            if (selectedNiche) {
                fetchOrganizations(selectedNiche.id);
            }
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la création';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    // Update subscription
    const handleUpdateSubscription = async () => {
        if (!selectedOrg) return;
        
        setSubmitting(true);
        try {
            await organizationsAPI.updateSubscription(selectedOrg.id, newSubscription);
            toast.success('Abonnement mis à jour!');
            setSubscriptionDialogOpen(false);
            if (selectedNiche) {
                fetchOrganizations(selectedNiche.id);
            }
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la mise à jour';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    // Auto-generate slug from name
    const handleNameChange = (name) => {
        setNewOrg({
            ...newOrg,
            name,
            slug: name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
        });
    };

    if (user?.role !== 'super_admin') {
        return (
            <Layout>
                <div className="flex items-center justify-center h-96">
                    <p className="text-muted-foreground">Accès réservé au Super Administrateur</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        {view === 'organizations' && selectedNiche ? (
                            <div className="flex items-center gap-3">
                                <Button variant="ghost" size="sm" onClick={handleBackToNiches}>
                                    <ArrowLeft className="w-4 h-4 mr-1" />
                                    Retour
                                </Button>
                                <div>
                                    <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-3">
                                        {React.createElement(NICHE_ICONS[selectedNiche.id] || Package, { 
                                            className: "w-8 h-8",
                                            style: { color: selectedNiche.color }
                                        })}
                                        {selectedNiche.name}
                                    </h1>
                                    <p className="text-muted-foreground mt-1">
                                        Gérez les boutiques de cette niche
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-3">
                                    <Crown className="w-8 h-8 text-amber-500" />
                                    OrderHub DZ - Super Admin
                                </h1>
                                <p className="text-muted-foreground mt-1">
                                    Gérez toutes vos niches et boutiques
                                </p>
                            </>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => view === 'niches' ? fetchNiches() : fetchOrganizations(selectedNiche?.id)}
                            disabled={loading}
                        >
                            <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
                            Actualiser
                        </Button>
                        {view === 'organizations' && (
                            <Button className="btn-luxury" onClick={() => setCreateDialogOpen(true)}>
                                <Plus className="w-4 h-4 mr-2" />
                                Nouvelle Boutique
                            </Button>
                        )}
                    </div>
                </div>

                {/* Platform Stats (only in niches view) */}
                {view === 'niches' && platformStats && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className="p-3 bg-blue-200 rounded-lg">
                                    <Building2 className="w-6 h-6 text-blue-700" />
                                </div>
                                <div>
                                    <p className="text-sm text-blue-600 font-medium">Total Boutiques</p>
                                    <p className="text-2xl font-bold text-blue-800">{platformStats.total_organizations}</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className="p-3 bg-green-200 rounded-lg">
                                    <CheckCircle className="w-6 h-6 text-green-700" />
                                </div>
                                <div>
                                    <p className="text-sm text-green-600 font-medium">Boutiques Actives</p>
                                    <p className="text-2xl font-bold text-green-800">{platformStats.active_organizations}</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className="p-3 bg-purple-200 rounded-lg">
                                    <ShoppingCart className="w-6 h-6 text-purple-700" />
                                </div>
                                <div>
                                    <p className="text-sm text-purple-600 font-medium">Total Commandes</p>
                                    <p className="text-2xl font-bold text-purple-800">{platformStats.total_orders}</p>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className="p-3 bg-amber-200 rounded-lg">
                                    <Users className="w-6 h-6 text-amber-700" />
                                </div>
                                <div>
                                    <p className="text-sm text-amber-600 font-medium">Total Utilisateurs</p>
                                    <p className="text-2xl font-bold text-amber-800">{platformStats.total_users}</p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Niches Grid View */}
                {view === 'niches' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {nicheStats.map((niche) => {
                            const NicheIcon = NICHE_ICONS[niche.id] || Package;
                            return (
                                <Card 
                                    key={niche.id}
                                    className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02] border-2 hover:border-primary/50"
                                    onClick={() => handleSelectNiche(niche)}
                                >
                                    <CardContent className="p-6">
                                        <div className="flex items-center gap-4 mb-4">
                                            <div 
                                                className="p-3 rounded-xl"
                                                style={{ backgroundColor: `${niche.color}20` }}
                                            >
                                                <NicheIcon 
                                                    className="w-8 h-8" 
                                                    style={{ color: niche.color }}
                                                />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-lg">{niche.name}</h3>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4 text-center">
                                            <div className="bg-muted/50 rounded-lg p-2">
                                                <p className="text-2xl font-bold">{niche.organization_count}</p>
                                                <p className="text-xs text-muted-foreground">Boutiques</p>
                                            </div>
                                            <div className="bg-muted/50 rounded-lg p-2">
                                                <p className="text-2xl font-bold">{niche.total_orders}</p>
                                                <p className="text-xs text-muted-foreground">Commandes</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Organizations List View */}
                {view === 'organizations' && (
                    <>
                        {/* Search */}
                        <Card>
                            <CardContent className="p-4">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Rechercher une boutique..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="pl-10"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Organizations Grid */}
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <div className="spinner" />
                            </div>
                        ) : filteredOrgs.length === 0 ? (
                            <Card>
                                <CardContent className="py-12 text-center text-muted-foreground">
                                    <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                    <p>Aucune boutique dans cette niche</p>
                                    <Button 
                                        className="mt-4" 
                                        onClick={() => setCreateDialogOpen(true)}
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Créer la première boutique
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {filteredOrgs.map((org) => {
                                    const plan = PLAN_LABELS[org.subscription_plan] || PLAN_LABELS.starter;
                                    const status = STATUS_LABELS[org.subscription_status] || STATUS_LABELS.trial;
                                    const StatusIcon = status.icon;
                                    
                                    return (
                                        <Card key={org.id} className="hover:shadow-md transition-shadow">
                                            <CardHeader className="pb-2">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <CardTitle className="text-lg">{org.name}</CardTitle>
                                                        <code className="text-xs bg-muted px-2 py-0.5 rounded mt-1 inline-block">
                                                            {org.slug}.orderhubdz.com
                                                        </code>
                                                    </div>
                                                    <Badge className={cn(status.color, 'flex items-center gap-1')}>
                                                        <StatusIcon className="w-3 h-3" />
                                                        {status.label}
                                                    </Badge>
                                                </div>
                                            </CardHeader>
                                            <CardContent className="space-y-4">
                                                {/* Stats */}
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                                                        <span>{org.order_count} commandes</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <Users className="w-4 h-4 text-muted-foreground" />
                                                        <span>{org.user_count} utilisateurs</span>
                                                    </div>
                                                </div>

                                                {/* Integrations */}
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-muted-foreground">Intégrations:</span>
                                                    {org.has_shopify && (
                                                        <Badge variant="outline" className="text-xs bg-green-50">
                                                            <Globe className="w-3 h-3 mr-1" />
                                                            Shopify
                                                        </Badge>
                                                    )}
                                                    {org.has_telegram && (
                                                        <Badge variant="outline" className="text-xs bg-blue-50">
                                                            <MessageSquare className="w-3 h-3 mr-1" />
                                                            Telegram
                                                        </Badge>
                                                    )}
                                                    {org.has_sms && (
                                                        <Badge variant="outline" className="text-xs bg-purple-50">
                                                            <Zap className="w-3 h-3 mr-1" />
                                                            SMS
                                                        </Badge>
                                                    )}
                                                    {!org.has_shopify && !org.has_telegram && !org.has_sms && (
                                                        <span className="text-xs text-muted-foreground">Aucune</span>
                                                    )}
                                                </div>

                                                {/* Actions */}
                                                <div className="flex gap-2 pt-2 border-t">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="flex-1"
                                                        onClick={() => {
                                                            setSelectedOrg(org);
                                                            setEditDialogOpen(true);
                                                        }}
                                                    >
                                                        <Edit className="w-3 h-3 mr-1" />
                                                        Modifier
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => {
                                                            setSelectedOrg(org);
                                                            setAdminDialogOpen(true);
                                                        }}
                                                    >
                                                        <UserPlus className="w-3 h-3" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => {
                                                            setSelectedOrg(org);
                                                            setNewSubscription({ plan: org.subscription_plan, duration_months: 1 });
                                                            setSubscriptionDialogOpen(true);
                                                        }}
                                                    >
                                                        <CreditCard className="w-3 h-3" />
                                                    </Button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                {/* Create Organization Dialog */}
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Building2 className="w-5 h-5" />
                                Nouvelle Boutique
                            </DialogTitle>
                            <DialogDescription>
                                Créez une nouvelle boutique dans la niche {selectedNiche?.name || 'sélectionnée'}
                            </DialogDescription>
                        </DialogHeader>
                        
                        <Tabs defaultValue="info" className="w-full">
                            <TabsList className="grid w-full grid-cols-3">
                                <TabsTrigger value="info">Informations</TabsTrigger>
                                <TabsTrigger value="shopify">Shopify</TabsTrigger>
                                <TabsTrigger value="notifications">Notifications</TabsTrigger>
                            </TabsList>
                            
                            <TabsContent value="info" className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label>Niche *</Label>
                                    <Select
                                        value={newOrg.niche}
                                        onValueChange={(v) => setNewOrg({ ...newOrg, niche: v })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Sélectionner une niche" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {niches.map((niche) => (
                                                <SelectItem key={niche.id} value={niche.id}>
                                                    {niche.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Nom de la boutique *</Label>
                                    <Input
                                        placeholder="Ex: Ma Boutique"
                                        value={newOrg.name}
                                        onChange={(e) => handleNameChange(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Sous-domaine (slug) *</Label>
                                    <div className="flex items-center gap-2">
                                        <Input
                                            placeholder="parfum-algerie"
                                            value={newOrg.slug}
                                            onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') })}
                                        />
                                        <span className="text-muted-foreground whitespace-nowrap">.orderhubdz.com</span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Email</Label>
                                        <Input
                                            type="email"
                                            placeholder="contact@boutique.dz"
                                            value={newOrg.email}
                                            onChange={(e) => setNewOrg({ ...newOrg, email: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Téléphone</Label>
                                        <Input
                                            placeholder="0555 XX XX XX"
                                            value={newOrg.phone}
                                            onChange={(e) => setNewOrg({ ...newOrg, phone: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Wilaya</Label>
                                    <Input
                                        placeholder="Alger"
                                        value={newOrg.wilaya}
                                        onChange={(e) => setNewOrg({ ...newOrg, wilaya: e.target.value })}
                                    />
                                </div>
                            </TabsContent>
                            
                            <TabsContent value="shopify" className="space-y-4 mt-4">
                                <div className="bg-muted/50 rounded-lg p-4 text-sm">
                                    <p className="font-medium mb-2">Configuration Shopify</p>
                                    <p className="text-muted-foreground">
                                        Connectez le compte Shopify de cette boutique pour synchroniser les produits et commandes.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <Label>URL de la boutique Shopify</Label>
                                    <Input
                                        placeholder="ma-boutique.myshopify.com"
                                        value={newOrg.shopify_shop_url}
                                        onChange={(e) => setNewOrg({ ...newOrg, shopify_shop_url: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Token d'accès Shopify</Label>
                                    <Input
                                        type="password"
                                        placeholder="shpat_xxxxx"
                                        value={newOrg.shopify_access_token}
                                        onChange={(e) => setNewOrg({ ...newOrg, shopify_access_token: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>URL du storefront (publique)</Label>
                                    <Input
                                        placeholder="https://www.ma-boutique.com"
                                        value={newOrg.shopify_storefront_url}
                                        onChange={(e) => setNewOrg({ ...newOrg, shopify_storefront_url: e.target.value })}
                                    />
                                </div>
                            </TabsContent>
                            
                            <TabsContent value="notifications" className="space-y-4 mt-4">
                                <div className="bg-muted/50 rounded-lg p-4 text-sm">
                                    <p className="font-medium mb-2">Configuration des notifications</p>
                                    <p className="text-muted-foreground">
                                        Configurez Telegram et Twilio SMS pour les notifications de cette boutique.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <Label>Token du bot Telegram</Label>
                                    <Input
                                        type="password"
                                        placeholder="123456789:ABCdefGHI..."
                                        value={newOrg.telegram_bot_token}
                                        onChange={(e) => setNewOrg({ ...newOrg, telegram_bot_token: e.target.value })}
                                    />
                                </div>
                                <div className="border-t pt-4 mt-4">
                                    <p className="font-medium text-sm mb-3">Twilio SMS</p>
                                    <div className="space-y-3">
                                        <div className="space-y-2">
                                            <Label>Account SID</Label>
                                            <Input
                                                placeholder="ACxxxxxxx"
                                                value={newOrg.twilio_account_sid}
                                                onChange={(e) => setNewOrg({ ...newOrg, twilio_account_sid: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Auth Token</Label>
                                            <Input
                                                type="password"
                                                placeholder="xxxxxxxx"
                                                value={newOrg.twilio_auth_token}
                                                onChange={(e) => setNewOrg({ ...newOrg, twilio_auth_token: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Messaging Service SID</Label>
                                            <Input
                                                placeholder="MGxxxxxxx"
                                                value={newOrg.twilio_messaging_service_sid}
                                                onChange={(e) => setNewOrg({ ...newOrg, twilio_messaging_service_sid: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>
                        </Tabs>
                        
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button className="btn-luxury" onClick={handleCreateOrg} disabled={submitting}>
                                {submitting ? 'Création...' : 'Créer la boutique'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Edit Organization Dialog */}
                <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Edit className="w-5 h-5" />
                                Modifier {selectedOrg?.name}
                            </DialogTitle>
                        </DialogHeader>
                        {selectedOrg && (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Nom</Label>
                                    <Input
                                        value={selectedOrg.name}
                                        onChange={(e) => setSelectedOrg({ ...selectedOrg, name: e.target.value })}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Email</Label>
                                        <Input
                                            type="email"
                                            value={selectedOrg.email || ''}
                                            onChange={(e) => setSelectedOrg({ ...selectedOrg, email: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Téléphone</Label>
                                        <Input
                                            value={selectedOrg.phone || ''}
                                            onChange={(e) => setSelectedOrg({ ...selectedOrg, phone: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Statut</Label>
                                    <Select
                                        value={selectedOrg.is_active ? 'true' : 'false'}
                                        onValueChange={(v) => setSelectedOrg({ ...selectedOrg, is_active: v === 'true' })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="true">Actif</SelectItem>
                                            <SelectItem value="false">Désactivé</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button className="btn-luxury" onClick={handleUpdateOrg} disabled={submitting}>
                                {submitting ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Create Admin Dialog */}
                <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <UserPlus className="w-5 h-5" />
                                Créer Admin pour {selectedOrg?.name}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Nom complet *</Label>
                                <Input
                                    placeholder="Nom de l'administrateur"
                                    value={newAdmin.name}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Email *</Label>
                                <Input
                                    type="email"
                                    placeholder="admin@example.com"
                                    value={newAdmin.email}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Mot de passe *</Label>
                                <Input
                                    type="password"
                                    placeholder="••••••••"
                                    value={newAdmin.password}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setAdminDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button className="btn-luxury" onClick={handleCreateAdmin} disabled={submitting}>
                                {submitting ? 'Création...' : 'Créer Admin'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Update Subscription Dialog */}
                <Dialog open={subscriptionDialogOpen} onOpenChange={setSubscriptionDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <CreditCard className="w-5 h-5" />
                                Abonnement - {selectedOrg?.name}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Plan</Label>
                                <Select
                                    value={newSubscription.plan}
                                    onValueChange={(v) => setNewSubscription({ ...newSubscription, plan: v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="starter">Starter (Gratuit)</SelectItem>
                                        <SelectItem value="pro">Pro (5,000 DA/mois)</SelectItem>
                                        <SelectItem value="enterprise">Enterprise (15,000 DA/mois)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Durée (mois)</Label>
                                <Select
                                    value={String(newSubscription.duration_months)}
                                    onValueChange={(v) => setNewSubscription({ ...newSubscription, duration_months: parseInt(v) })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="1">1 mois</SelectItem>
                                        <SelectItem value="3">3 mois</SelectItem>
                                        <SelectItem value="6">6 mois</SelectItem>
                                        <SelectItem value="12">12 mois</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setSubscriptionDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button className="btn-luxury" onClick={handleUpdateSubscription} disabled={submitting}>
                                {submitting ? 'Mise à jour...' : 'Mettre à jour'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
